import { Link } from 'react-router-dom'
import { Botao } from '../../../components/Botao'
import { IconeImprimir, IconeSeta } from '../../../components/Icones'
import { TERMOS, TEXTOS } from '../../../lib/glossario'

/**
 * Os rótulos desta barra moram aqui, e não no glossário.
 *
 * A regra do glossário é para o texto que aparece em mais de uma tela — é ela que evita
 * "Enviar ao comitê" numa tela e "Submeter RDM" noutra. Estes três só existem nesta barra,
 * que só existe nesta página; levá-los para `glossario.ts` só espalharia a busca por quem
 * usa o quê. O que é compartilhado (`TEXTOS.relatorio.ajuda`, a sigla RDM) continua vindo
 * de lá.
 */
const CONTROLES = {
  voltar: 'Voltar para a solicitação',
  imprimir: 'Imprimir / Salvar em PDF',
  comoSalvar: 'Na janela de impressão, escolha "Salvar como PDF" no lugar da impressora para gerar o arquivo.',
} as const

interface Props {
  /** Para onde o "voltar" leva — a solicitação, ou a lista quando o endereço é inválido. */
  voltarPara: string
  /** Só há o que imprimir depois de o documento carregar. */
  pronto: boolean
}

/**
 * A barra de tela do relatório — tudo o que NÃO sai no papel.
 *
 * `print:hidden` e não uma classe do `relatorio.css`: esta barra é interface da aplicação,
 * feita com os componentes da casa, e a exceção do CSS dedicado vale para o documento. O
 * jeito de saber se algo pertence ao documento é perguntar se sai impresso.
 *
 * A explicação do que é o documento fica aqui em cima porque o público inclui fiscais de
 * contrato e servidores que não são de TI: quem chega neste endereço vindo do fim de um
 * cadastro precisa saber o que está vendo, para que serve e como salvar em PDF — nesta
 * ordem, antes de clicar.
 *
 * Imprimir é `<button>` (é uma ação nesta página, não um endereço) e voltar é `<Link>` (é um
 * endereço, e transformá-lo em botão tiraria o "abrir em nova aba" e o clique do meio de
 * quem usa o navegador).
 *
 * @param props destino do voltar e se o documento já carregou
 * @returns a barra de controles
 */
export function ControlesDoRelatorio({ voltarPara, pronto }: Props) {
  return (
    <section
      aria-label="Ações do relatório"
      className="sticky top-0 z-10 border-b border-slate-300 bg-white px-4 py-3 shadow-sm print:hidden"
    >
      <div className="mx-auto flex max-w-[210mm] flex-wrap items-center gap-x-4 gap-y-3">
        <Link
          to={voltarPara}
          className={
            'inline-flex items-center gap-1.5 text-sm text-niteroi-700 underline underline-offset-2 ' +
            'hover:text-niteroi-900'
          }
        >
          <IconeSeta className="h-4 w-4 rotate-180" />
          {CONTROLES.voltar}
        </Link>

        <Botao aparencia="primario" onClick={() => window.print()} disabled={!pronto}>
          <IconeImprimir className="h-4 w-4" />
          {CONTROLES.imprimir}
        </Botao>

        <p className="min-w-[16rem] flex-1 text-sm text-slate-600">
          <strong className="font-semibold text-slate-800">{TERMOS.rdm.sigla}.</strong> {TEXTOS.relatorio.ajuda}{' '}
          {CONTROLES.comoSalvar}
        </p>
      </div>
    </section>
  )
}
